import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 設定できる権限（サーバの ApiPermissionRight と同じ欄）。 */
export interface PermissionRight {
    readonly field: string;
    readonly name: string;
    readonly description: string;
}

/** 権限を持つ人 1 人（サーバの ApiPermissionRow と同じ欄）。<b>触れるかはサーバが決める。</b> */
export interface PermissionRow {
    readonly email: string;
    readonly source: string;
    readonly editable: boolean;
    readonly isSelf: boolean;
    readonly checks: readonly { readonly field: string; readonly checked: boolean; readonly canToggle: boolean }[];
}

/** <c>GET /api/admin-center/permissions</c>。 */
export interface PermissionList {
    readonly rights: readonly PermissionRight[];
    readonly rows: readonly PermissionRow[];
}

const nameOf = (rights: readonly PermissionRight[], field: string): string =>
    rights.find((o) => o.field === field)?.name ?? field;

/** ダイアログに出す権限の名前（「・」で区切る。1 つも無ければ「なし」）。 */
const namesOf = (names: readonly string[]): string => (names.length === 0 ? "なし" : names.join("・"));

/**
 * 最後の確認はブラウザの確認ダイアログで行う（重要な操作は最後にダイアログで確かめる。2026-10-10 に利用者が決めた）。
 * 運営の権限の付け外しは、管理者画面でできることが変わる。
 */
export const addPermissionDialog = (email: string, names: readonly string[]): string =>
    `「${email}」に管理者画面の権限（${namesOf(names)}）を付けます。よろしいですか？`;

export const changePermissionDialog = (email: string, names: readonly string[]): string =>
    `「${email}」の管理者画面の権限を（${namesOf(names)}）に変更します。外した権限の操作はできなくなります。よろしいですか？`;

function AddForm({ rights }: { readonly rights: readonly PermissionRight[] }): ReactElement {
    const form = useApiForm(adminCenterApi.permissions, undefined, (valueOf) => addPermissionDialog(
        valueOf("Email"),
        rights.filter((right) => valueOf(right.field) === "true").map((right) => right.name),
    ));

    return (
        <div className="card mb-4 p-6">
            <h2 className="section-title">追加する</h2>
            <p className="mb-4 text-sm text-slate-500">
                対象の方には、先にポータルのアカウントを作っていただく必要があります。
            </p>
            <form method="post" onSubmit={form.onSubmit} className="grid gap-4">
                <FormSummary messages={form.summary} />
                <div>
                    <label className="form-label" htmlFor="newEmail">メールアドレス</label>
                    <input className="form-input" id="newEmail" name="Email" type="email" required />
                </div>
                <div className="flex flex-wrap gap-4">
                    {rights.map((right) => (
                        <label className="flex items-center gap-2 text-sm" key={right.field}>
                            <input type="checkbox" className="form-check" name={right.field} value="true" />
                            <span>{right.name}</span>
                        </label>
                    ))}
                </div>
                <div>
                    <button type="submit" className="btn-primary" disabled={form.busy}>追加する</button>
                </div>
            </form>
        </div>
    );
}

function RowForm({ row, rights }: { readonly row: PermissionRow; readonly rights: readonly PermissionRight[] }): ReactElement {
    // 触れないチェック（ご自身の「権限管理」など）は、いまの値のまま
    const form = useApiForm(adminCenterApi.permissions, undefined, (valueOf) => changePermissionDialog(
        row.email,
        row.checks
            .filter((check) => (check.canToggle ? valueOf(check.field) === "true" : check.checked))
            .map((check) => nameOf(rights, check.field)),
    ));

    return (
        <section className="card mb-4 p-6">
            <form method="post" onSubmit={form.onSubmit}>
                <FormSummary messages={form.summary} />
                <input type="hidden" name="Email" value={row.email} />

                <div className="mb-4">
                    <p className="font-semibold">{row.email}</p>
                    <p className="text-xs text-slate-500">{row.source}</p>
                </div>

                <div className="mb-4 flex flex-wrap gap-4">
                    {row.checks.map((check) => (
                        <label className="flex items-center gap-2 text-sm" key={check.field}>
                            <input
                                type="checkbox" className="form-check" name={check.field} value="true"
                                defaultChecked={check.checked} disabled={!check.canToggle}
                            />
                            <span>{nameOf(rights, check.field)}</span>
                        </label>
                    ))}
                </div>

                {/* 触れないチェックは送られない。何が変わらないのかを言葉でも出す */}
                {row.editable
                    ? (
                        <>
                            {row.isSelf
                                ? (
                                    <>
                                        <p className="mb-3 text-xs text-slate-500">
                                            ご自身の「権限管理」は外せません。
                                            外すと、その場から権限を設定できなくなるためです。
                                        </p>
                                        {/* disabled は送られないので、値を hidden で補う */}
                                        <input type="hidden" name="CanManagePermissions" value="true" />
                                    </>
                                )
                                : null}
                            <button type="submit" className="btn-secondary btn-sm" disabled={form.busy}>保存する</button>
                        </>
                    )
                    : <p className="text-xs text-slate-500">設定ファイルに書かれているため、画面からは変えられません。</p>}
            </form>
        </section>
    );
}

/**
 * 権限の管理。<b>権限管理を持つ人だけ</b>（API が判定）。
 * 保存すると同じ画面を開き直し、結果を通知に出す。
 */
export function PermissionsPage(): ReactElement | null {
    const data = useApiData<PermissionList>(adminCenterApi.permissions);
    usePageMeta(titleFor(data, "権限の管理"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="権限の一覧" />;
    }
    const { rights, rows } = data.value;

    return (
        <div className="page max-w-3xl">
            <p className="mb-4"><Link to={adminCenterUrls.menu} className="btn-secondary btn-sm">管理センターへ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">権限の管理</h1>
                <p className="page-lead">
                    管理者画面でできることです。テナントの中の権限（契約者・管理者）とは別です。
                </p>

                <div className="card mb-4 p-6">
                    <h2 className="section-title">できること</h2>
                    <dl className="grid gap-2 text-sm">
                        {rights.map((right) => (
                            <div key={right.field}>
                                <dt className="font-semibold">{right.name}</dt>
                                <dd className="text-slate-500">{right.description}</dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <AddForm rights={rights} />

                {rows.map((row) => <RowForm key={row.email} row={row} rights={rights} />)}

                <div className="note-panel">
                    <p className="text-sm">
                        設定ファイルに書いた方は、3 つとも持ち、画面からは変えられません。
                        全員を外してしまうと誰も権限を設定できなくなり、
                        復旧手段がデータベースを直接操作することだけになるためです。
                    </p>
                </div>
            </div>
        </div>
    );
}
